/* eslint-disable no-prototype-builtins */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  useToast,
  VStack
} from '@chakra-ui/react'
import { useAuthState } from '../../../../../../../auth/AuthProvider'
import { type Patient } from '../../../../../../../model/patient'
import { useAppDispatch } from '../../../../../../../store/hooks'
import { useState } from 'react'
import {
  addPatient,
  updateCurrentPatient
} from '../../../../../../../reducers/patientsReducer'
import { setCurrentPatient } from '../../../../../../../reducers/currentPatientReducer'
import { getPatientService } from '../../../../../../../services/Services'
import Camera, { FACING_MODES } from 'react-html5-camera-photo'
import { Form } from 'react-final-form'
import InputField from '../../../../../../../components/common/form/InputField'
import DateField from '../../../../../../../components/common/form/DateField/DateField'
import FilePicker from '../../../../../../../components/common/FilePicker/FilePicker'
import RadioField from '../../../../../../../components/common/form/RadioField'
import { GENDER_OPTIONS } from './constants'
import {
  composeValidators,
  isValidDate,
  required
} from '../../../../../../../components/common/form/validators'
import { ArrowsRightLeftIcon, CameraIcon } from '@heroicons/react/24/outline'
import TextareaField from '../../../../../../../components/common/form/TextareaField'
import { toCamelCase } from '../../../../../../../utils/schemaUtils'
import { type DoctorType } from '../../../../../../../model/doctor'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import messages from './messages'
import { CountrySelectField } from '../../../../../../../components/common/form/CountrySelect'

interface Props {
  historySchema: DoctorType['schema']['history']
  isEditMode?: boolean
  currentPatient?: Patient
  onClose: () => void
}

export default function PatientForm({
  historySchema,
  isEditMode = false,
  currentPatient = undefined,
  onClose
}: Props) {
  const dispatch = useAppDispatch()
  const { uid } = useAuthState()
  const toast = useToast()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const [uploadedFiles, setUploadedFiles] = useState<any[]>()
  const [uploadedCaptures, setUploadedCaptures] = useState<any[]>([])
  const [camera, setCamera] = useState(false)
  const [cameraView, setCameraView] = useState('user')

  const handlePatientSubmit = (newValues: any, form: any) => {
    if (isEditMode) {
      const updatedValues: any = {}

      // todo extract this logic
      for (const key in currentPatient) {
        if (newValues.hasOwnProperty(key)) {
          updatedValues[key] = newValues[key] // its in old and new, means it was updated
        } else {
          updatedValues[key] = null // its in old, but not in new, means it was cleared
        }
      }

      for (const key in newValues) {
        if (!currentPatient?.hasOwnProperty(key)) {
          updatedValues[key] = newValues[key] // its in new but not old, means it is completely new
        }
      }

      getPatientService()
        .updatePatient({
          id: currentPatient?.id,
          attachments: uploadedFiles,
          captures: uploadedCaptures,
          ...updatedValues
        })
        .then((response) => {
          dispatch(updateCurrentPatient(response))
          dispatch(setCurrentPatient(response))
          toast({
            title: formatMessage(messages.patientUpdated),
            status: 'success',
            duration: 5000,
            isClosable: true
          })
        })
    } else {
      getPatientService()
        .createPatient({
          ...newValues,
          ownerId: uid,
          attachments: uploadedFiles,
          captures: uploadedCaptures
        })
        .then((response) => {
          dispatch(addPatient(response))
          dispatch(setCurrentPatient(response))
          toast({
            title: formatMessage(messages.patientCreated),
            status: 'success',
            duration: 5000,
            isClosable: true
          })
          navigate(`/patients/${response.id}`)
          form.reset()
        })
    }
    handleClose()
  }

  const handleFilesChange = (files: File[]) => {
    setUploadedFiles(files)
  }

  const handleTakePhoto = (dataUri: any) => {
    setUploadedCaptures((prev: any) => [...prev, dataUri])
  }

  const toggleCamera = () => {
    setCamera((prev) => !prev)
  }

  const toggleCameraView = () => {
    if (cameraView === FACING_MODES.ENVIRONMENT) {
      setCameraView(FACING_MODES.USER)
    } else {
      setCameraView(FACING_MODES.ENVIRONMENT)
    }
  }

  const handleClose = () => {
    setCamera(false)
    setUploadedCaptures([])
    setUploadedFiles([])
    onClose()
  }

  return (
    <Form
      onSubmit={handlePatientSubmit}
      initialValues={currentPatient ?? { nationality: 'EG' }}
      render={({ handleSubmit, hasValidationErrors }) => (
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch" px={6} py={6}>
            {/* Personal Information */}
            <Box
              p={6}
              bg="white"
              boxShadow="sm"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="md" fontWeight="semibold" mb={2}>
                  Personal Information
                </Heading>
                <Divider />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} pt={2}>
                  <InputField
                    name="name"
                    label={formatMessage(messages.patientName)}
                    validate={required}
                  />
                  <DateField
                    name="dateOfBirth"
                    label={formatMessage(messages.dateOfBirth)}
                    validate={composeValidators(required, isValidDate)}
                    outputFormat="DD.MM.YYYY"
                  />
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <CountrySelectField
                    name="nationality"
                    label={formatMessage(messages.nationality)}
                  />
                  <RadioField
                    name="sex"
                    label={formatMessage(messages.sex)}
                    options={GENDER_OPTIONS}
                    validate={composeValidators(required)}
                  />
                </SimpleGrid>
              </VStack>
            </Box>

            {/* Health History */}
            <Box
              bg="white"
              boxShadow="sm"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
              overflow="hidden"
            >
              <Accordion allowToggle>
                <AccordionItem border="none">
                  <h2>
                    <AccordionButton py={4} _hover={{ bg: 'gray.50' }}>
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontWeight="semibold"
                        fontSize="md"
                      >
                        {historySchema.displayName}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>

                  <AccordionPanel pb={6} px={6}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {historySchema.fields.map((field: any) => (
                        <TextareaField
                          key={field.id}
                          name={`history.${toCamelCase(field.displayName)}`}
                          label={field.displayName}
                          description={field.description}
                        />
                      ))}
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>

            {/* Contact Information */}
            <Box
              p={6}
              bg="white"
              boxShadow="sm"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="md" fontWeight="semibold" mb={2}>
                  Contact Information
                </Heading>
                <Divider />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} pt={2}>
                  <InputField
                    name="phoneNumber"
                    label={formatMessage(messages.phoneNumber)}
                  />
                  <InputField
                    name="address"
                    label={formatMessage(messages.address)}
                  />
                </SimpleGrid>
              </VStack>
            </Box>

            {/* Attachments & Photos */}
            <Box
              p={6}
              bg="white"
              boxShadow="sm"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="md" fontWeight="semibold" mb={2}>
                  Media & Attachments
                </Heading>
                <Divider />
                <Box pt={2}>
                  <Box mb={6}>
                    <FilePicker onChange={handleFilesChange} multiple />
                  </Box>
                  <Flex gap={3} flexWrap="wrap">
                    <Button
                      colorScheme="blue"
                      size="md"
                      onClick={toggleCamera}
                      leftIcon={<CameraIcon color="white" width="20px" />}
                    >
                      {camera
                        ? formatMessage(messages.turnOffCamera)
                        : formatMessage(messages.takePhotos)}
                    </Button>
                    <Button
                      variant="outline"
                      size="md"
                      leftIcon={
                        <ArrowsRightLeftIcon
                          style={{ width: '20px', height: '20px' }}
                        />
                      }
                      onClick={toggleCameraView}
                    >
                      {formatMessage(messages.toggleCamera)}
                    </Button>
                  </Flex>
                </Box>

                {camera && (
                  <Box borderRadius="lg" overflow="hidden" borderWidth="1px">
                    <Camera
                      onTakePhoto={(dataUri) => {
                        handleTakePhoto(dataUri)
                      }}
                      idealFacingMode={cameraView as any}
                    />
                  </Box>
                )}

                {uploadedCaptures.length > 0 && (
                  <SimpleGrid columns={{ base: 3, sm: 4, md: 6 }} spacing={2}>
                    {uploadedCaptures.map((capture: any, index: number) => (
                      <Box
                        key={index}
                        borderRadius="md"
                        overflow="hidden"
                        borderWidth="1px"
                      >
                        <img
                          src={capture}
                          alt={`capture-${index}`}
                          style={{ width: '100%', height: 'auto' }}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </VStack>
            </Box>
          </VStack>

          <Flex
            position="sticky"
            bottom={0}
            justifyContent="flex-end"
            py={4}
            px={6}
            borderTopWidth="1px"
            borderColor="gray.100"
            gap={3}
            bg="white"
            zIndex={10}
          >
            <Button variant="ghost" size="lg" onClick={onClose}>
              {formatMessage(messages.close)}
            </Button>
            <Button
              colorScheme="blue"
              size="lg"
              px={10}
              type="submit"
              isDisabled={hasValidationErrors}
            >
              {formatMessage(messages.save)}
            </Button>
          </Flex>
        </form>
      )}
    />
  )
}
